// session.js - The one order this browser knows about, kept on the device.
//
// There is no customer account and there should not be one. A password is a
// thing to forget, a thing to reset, and a second place to leak a name and an
// address, and the whole relationship is one order lasting about two weeks. The
// tracking token in the emailed link is the credential, and this file is the
// device remembering it so nobody has to go and find that email again.
//
// It holds what the tracking link already holds, in the browser that already
// followed it. That is the security argument in full: the token is in this
// browser's history, in its bookmarks and in the mailbox open in the next tab,
// so writing it into localStorage on the same origin does not widen anything.
//
// The one case it does change is a shared computer, so it expires, and every
// screen that acts on it gives a way to say this is not me. `forget()` is not a
// tidy up function, it is a feature.
//
// Never used to decide what a page is allowed to show. The server checks the
// token on every request; this only saves somebody typing.
//
// The same idea covers the affiliate page, with one difference worth knowing:
// an order's tracking token never expires and an affiliate's pass is signed for
// thirty days. So the pass is kept without a date of its own and the server is
// what decides it has run out, which it says by refusing.
//
// LEGEND
//   1. remember()          keep an order after the customer proved it is theirs
//   2. remembered()        the order this device knows about, or null
//   3. forget()            drop it, on request or when the server stops knowing it
//   4. rememberAffiliate() the same three, for the affiliate page
//   5. rememberedAffiliate()
//   6. forgetAffiliate()

const KEY = "igs.order.v1";

// Long enough for somebody who sat on the mailer for a month, short enough that
// a family computer forgets. Any order takes weeks, not seasons.
const MAX_AGE = 180 * 24 * 3600_000;

////////// 1. REMEMBER //////////

export function remember(order) {
  const token = order?.token || String(order?.trackingUrl || "").split("/track/")[1];
  if (!token || !order?.number) return null;

  const kept = { number: order.number, token, at: Date.now() };
  try {
    localStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    // A private window, or storage switched off. Nothing here is load bearing.
  }
  return kept;
}

////////// 2. READ IT BACK //////////
// Returns null rather than throwing, for every reason it can fail: no storage,
// a value somebody edited by hand, an entry older than the process it belongs
// to. Callers treat null as "we do not know this person", which is the same
// thing every one of them did before this file existed.

export function remembered() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const kept = JSON.parse(raw);
    if (!kept?.token || !kept?.number) return null;
    if (!kept.at || Date.now() - kept.at > MAX_AGE) {
      forget();
      return null;
    }
    return kept;
  } catch {
    return null;
  }
}

////////// 3. FORGET //////////

export function forget() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do about it and nothing depends on it.
  }
}

////////// 4. THE AFFILIATE PASS //////////
// A signed token with an expiry inside it, so there is no second copy of the
// date out here to disagree with the one the server reads. When it runs out the
// page gets a 401, drops it, and asks for a new one.

const AFFILIATE_KEY = "igs.affiliate.v1";

export function rememberAffiliate(token) {
  if (!token) return;
  try {
    localStorage.setItem(AFFILIATE_KEY, String(token));
  } catch {
    // Private window, or storage off. They sign in again next time.
  }
}

export function rememberedAffiliate() {
  try {
    return localStorage.getItem(AFFILIATE_KEY) || null;
  } catch {
    return null;
  }
}

export function forgetAffiliate() {
  try {
    localStorage.removeItem(AFFILIATE_KEY);
  } catch {
    // Nothing depends on it.
  }
}
